/** Helpers for compose / reply / forward. */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeEmailAddress(raw) {
  return String(raw || "")
    .trim()
    .replace(/^.*<([^>]+)>.*$/, "$1")
    .trim();
}

export function isValidEmail(raw) {
  const email = normalizeEmailAddress(raw);
  return !!email && EMAIL_RE.test(email);
}

export function parseEmailList(raw) {
  return String(raw || "")
    .split(/[,;\n]+/)
    .map((s) => normalizeEmailAddress(s))
    .filter((e) => isValidEmail(e));
}

export function stripHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function appendSignature(bodyHtml, signatureHtml) {
  const body = String(bodyHtml || "").trim();
  const sig = String(signatureHtml || "").trim();
  if (!sig) return body;
  const divider = body ? "<br><br>" : "";
  return `${body}${divider}<div data-crm-signature="1">${sig}</div>`;
}

export function buildForwardQuote(msg) {
  const from = msg.fromName ? `${msg.fromName} &lt;${msg.fromAddress}&gt;` : (msg.fromAddress || "");
  const date = msg.sentAt ? new Date(msg.sentAt).toLocaleString() : "";
  const to = (msg.toAddresses || []).join(", ");
  const body = msg.bodyHtml || `<pre>${stripHtml(msg.bodyText || msg.snippet || "")}</pre>`;
  return (
    `<br><br><div style="border-left:2px solid #ccc;padding-left:12px;margin-top:12px;color:#555;">`
    + `<div style="font-size:12px;margin-bottom:8px;">`
    + `---------- Forwarded message ----------<br>`
    + `From: ${from}<br>`
    + `Date: ${date}<br>`
    + `Subject: ${msg.subject || ""}<br>`
    + (to ? `To: ${to}<br>` : "")
    + `</div>${body}</div>`
  );
}

/**
 * AI drafts come back as plain text. Turn them into the paragraph HTML the
 * TipTap compose editor expects: blank-line-separated blocks become <p>,
 * single newlines become <br>. Everything is escaped first — a draft is model
 * output and must never be trusted as markup.
 */
export function textToComposeHtml(text) {
  const esc = (s) => String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return String(text || "")
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((block) => esc(block.trim()).replace(/\n/g, "<br>"))
    .filter(Boolean)
    .map((block) => `<p>${block}</p>`)
    .join("");
}

/**
 * Split a leading `Subject: …` line off an AI draft. We ask for one only when
 * composing a brand-new email with no subject yet; every other mode gets body
 * text alone, in which case `subject` comes back empty.
 */
export function splitDraftSubject(text) {
  const raw = String(text || "");
  const m = raw.match(/^\s*subject\s*:\s*(.+?)[ \t]*(?:\n|$)/i);
  if (!m) return { subject: "", body: raw.trim() };
  return { subject: m[1].trim(), body: raw.slice(m[0].length).trim() };
}

export function fwdSubject(subject) {
  const s = String(subject || "").trim();
  if (!s) return "Fwd:";
  if (/^fwd:/i.test(s)) return s;
  return `Fwd: ${s}`;
}

export function replyAllRecipients(msg, accountEmail) {
  const me = normalizeEmailAddress(accountEmail).toLowerCase();
  const norm = (a) => normalizeEmailAddress(a).toLowerCase();
  const dedupe = (arr) => arr.filter(Boolean).filter((a, i, s) => s.indexOf(a) === i);

  const from = norm(msg.fromAddress);
  const originalTo = dedupe((msg.toAddresses || []).map(norm)).filter((a) => a !== me);

  // Replying-all to a message you sent yourself: `from` is your own mailbox, so
  // there is no counterparty to promote into To. Keep the original To line as To
  // instead of letting everyone fall through into Cc (which leaves To empty and
  // silently disables Send).
  const to = from && from !== me ? [from] : originalTo;

  const cc = dedupe([
    ...(msg.toAddresses || []).map(norm),
    ...(msg.ccAddresses || []).map(norm),
  ]).filter((a) => a !== me && !to.includes(a));

  return { to, cc };
}
